import React, { useState, useEffect, useMemo, useRef } from 'react';
import { ccmApi, api } from '../lib/api';
import { ScanSuggest } from '../components/scan-suggest.jsx';
import { TimeEntriesModal } from '../components/time-entries-modal.jsx';
import { BhiAssessmentModal } from '../components/bhi-assessment-modal.jsx';
import { PainAssessmentModal } from '../components/pain-assessment-modal.jsx';
import { suggestBhi } from '../lib/bhi-suggest';
import { severityCssToken } from '../lib/bhi-assessment-entry';
import { buildPainAssessmentEntry } from '../lib/pain-assessment-entry';
import { latestPainAssessment } from '../lib/pain-scales';
import { entryDateForMonth } from '../lib/month';
import { suggestForPatientWithContext, aiChipsForPatient, presetApplicability } from '../lib/suggest';
import { chronicCount } from '../lib/ai-suggest';
import {
  targetForProgram,
  programLabelFor,
  conditionLabel,
  isWorkflowPatient,
  isEnrolledProgram,
  normalizeProgram,
  isActivePatient,
  isWorkflowExcluded,
  bhiActivityMinutes,
  ccmActivityMinutes,
  isBhiEnrolledPatient,
  bhiTargetForProgram,
} from '../lib/workflow';
import { operationalForFilter, targetForRow } from '../lib/time-logs-bhi-predicate';
import { CCM_MILESTONES, isCcmTierPatient, ccmMilestoneProgress, simpleTargetColor, ccmRunwayLens } from '../lib/ccm-tier-progress';
import { rowPace } from '../lib/month-pace';
import { resolveBhiBillCode } from '../lib/bhi-billing';
import { cptForPreset, canLogPresetForPatient } from '../lib/preset-program';
import { untaggedBhCandidates, retagEntryToBhi } from '../lib/bhi-retag';
import { isPainEnrolledPatient, cpmMonthlyEvidence, PAIN_BASE_TARGET_MIN } from '../lib/pain-billing';
import { isPinEnrolledPatient, pinMonthlyMinutes, NAV_BASE_TARGET_MIN } from '../lib/pin-billing';
import { BhiRetagModal } from '../components/bhi-retag-modal.jsx';
import {
  DEFAULT_PRESETS,
  BHI_DEFAULT_PRESETS,
  PAIN_DEFAULT_PRESETS,
  PIN_DEFAULT_PRESETS,
  normalizePresets,
  presetsForSettings,
  normalizeBhiPresets,
  bhiPresetsForSettings,
  normalizePainPresets,
  normalizePinPresets,
  effectivePresetsFor,
} from '../lib/quick-log-presets';
import { metCountsByProgram } from '../lib/all-lens-metrics';
import { entryProgramFamily } from '../lib/bhi-billing';

// Inline init helper (was in hifi-data.jsx in the prototype)
function init(name) {
  return (name || '').split(' ').map(s => s[0]).slice(0, 2).join('').toUpperCase();
}

function presetActivityNote(preset) {
  const note = String(preset?.notes ?? preset?.description ?? preset?.desc ?? preset?.hint ?? '').trim();
  return note;
}

function isDeliveryMode(mode) {
  return mode === 'in-person' || mode === 'telehealth';
}

function deliveryModeShort(mode) {
  return mode === 'telehealth' ? 'TH' : mode === 'in-person' ? 'IP' : '—';
}

// Stable avatar slot from patient id
function avSlotFor(id) {
  const tail = String(id || '').slice(-2);
  const n = parseInt(tail, 16);
  if (!Number.isFinite(n)) return 1;
  return (n % 6) + 1;
}

// Deterministic advisory band — clinicians should read this as signal
// strength, not probability. Randomized confidence percentages were
// misleading (the same scan produced different numbers run-to-run).
//   - "High":   both rule engine and LLM identified the same chip.
//   - "Medium": LLM-only (engine missed it) or rule-only with context
//                (lab reports or a latest visit note backing the heuristic).
//   - "Low":    rule-only with no evidence (latest visit note + lab signals
//                both absent for this patient).
function classifyConfidence({ _llm, llmConfirmed, hasContext }) {
  if (llmConfirmed) return 'High';
  if (_llm) return 'Medium';
  if (hasContext) return 'Medium';
  return 'Low';
}

// Adapt a raw patient row from the API into the flat shape used in the UI.
// GET /data/patients returns flat shape (data fields at top level + _updatedAt).
// We also tolerate a wrapped { id, data: {...} } shape defensively.
// _raw holds the full patient data so addTime/undoEntries can PUT it back intact.
// _raw._updatedAt is preserved so PUTs can carry it back for optimistic locking
// — the server compares it against the row's current updated_at and 409s if
// stale.
function adaptPatient(row, monthKey) {
  if (!row || typeof row !== 'object') return null;
  const data = row.data && typeof row.data === 'object' ? row.data : row;
  // _updatedAt may live on the top-level row (flat shape, current prod), on
  // row.data (wrapped shape, defensive), or as the canonical Postgres column
  // `updated_at` on the row. Prefer the most specific.
  const updatedAt = data._updatedAt || row._updatedAt || row.updated_at || null;
  const fname = data.fname || '';
  const lname = data.lname || '';
  const name = `${fname} ${lname}`.trim() || data.name || data.mrn || row.id;
  const program = data.billing?.program || '';
  const conditions = Array.isArray(data.conditions) ? data.conditions : [];
  const dx = conditions.map(conditionLabel).filter(Boolean);
  const ccmTarget = targetForProgram(program);
  const bhiTarget = bhiTargetForProgram(resolveBhiBillCode(data, monthKey));
  // Pain (CPM) slot: 30 practitioner-minutes when enrolled, else no slot.
  const painTarget = isPainEnrolledPatient(data) ? PAIN_BASE_TARGET_MIN : 0;
  const pinTarget = isPinEnrolledPatient(data) ? NAV_BASE_TARGET_MIN : 0;
  return {
    id: row.id,
    name,
    mrn: data.mrn || '',
    dx,
    // Canonical program string: '' for not-enrolled, otherwise UPPER-CASED.
    // Downstream code should check `isEnrolledProgram(p.program)` rather
    // than comparing to legacy `'none'`.
    program: normalizeProgram(program),
    programLabel: programLabelFor(program),
    target: ccmTarget,    // legacy alias (= targets.ccm) — kept for back-compat with non-page consumers
    targets: { ccm: ccmTarget, bhi: bhiTarget, pain: painTarget, pin: pinTarget },
    logged: ccmActivityMinutes(data, monthKey),
    consent: data.consent || 'pending',
    avSlot: avSlotFor(row.id),
    // Workflow-eligibility flags derived from the raw Elation-synced blob.
    // Together with `program`, they let the roster filter mirror v2 semantics
    // (active + not declined + not explicitly workflow-excluded + enrolled).
    active: isActivePatient(data),
    workflowExcluded: isWorkflowExcluded(data),
    // Elation tags (e.g. ["ccm", "echo health"]) — used by the Tag filter chip
    // row. Stored as a normalized lower-cased array so filter comparisons stay
    // case-insensitive. Display capitalization is restored at render time.
    tags: Array.isArray(data.tags)
      ? data.tags.filter(Boolean).map(t => String(t).trim().toLowerCase())
      : [],
    // Keep the full patient data blob (with _updatedAt) so we can PUT it back
    // with the updated activityLog AND the optimistic-lock timestamp.
    _raw: { ...data, _updatedAt: updatedAt },
  };
}

// Title-case for tag display ("echo health" → "Echo Health"). Filter comparisons
// stay on the lower-cased value so display formatting can't break matching.
function tagDisplay(t) {
  return String(t || '').replace(/\b\w/g, c => c.toUpperCase());
}

function staffNameForUser(user) {
  return String(user?.displayName || user?.name || user?.email || '').trim();
}

export function TimeLogsPage({
  month,
  focusPatient,
  onFocusConsumed,
  onOpenPatientInStudio,
  hideNoChronic,
  currentUser,
  programFilter = 'ccm',
  bhiEnabled = false,
  painEnabled = false,
  pinEnabled = false,
}) {
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [tagFilter, setTagFilter] = useState('all');
  const [presets, setPresets] = useState(() => normalizePresets(DEFAULT_PRESETS));
  const [bhiPresets, setBhiPresets] = useState(() => normalizeBhiPresets(BHI_DEFAULT_PRESETS));
  // Pain presets are defaults-only (not admin-editable yet); per-patient chip
  // applicability keeps them hidden unless billing.painProgram is set.
  const painPresets = useMemo(() => normalizePainPresets(PAIN_DEFAULT_PRESETS), []);
  const pinPresets = useMemo(() => normalizePinPresets(PIN_DEFAULT_PRESETS), []);
  const effectivePresets = useMemo(
    () => effectivePresetsFor(
      programFilter,
      presets,
      bhiPresets,
      painEnabled ? painPresets : [],
      pinEnabled ? pinPresets : []
    ),
    [programFilter, presets, bhiPresets, painPresets, painEnabled, pinPresets, pinEnabled]
  );
  const [logged, setLogged] = useState({}); // pid -> minutes
  // Full staff list (credentials included). Fetched ONCE by the credential-
  // stamping effect below and shared with the pain lens, whose per-row minutes
  // come from cpmMonthlyEvidence(p._raw, month, staffList) — designated-
  // provider counting needs credentials for legacy (unstamped) entries.
  const [staffList, setStaffList] = useState(null);
  // Parallel BHI-only minute totals used for the All-lens per-row split display
  // (`CCM Xm · BHI Ym`). Total CCM minutes are derived as logged[id] - bhiLogged[id]
  // in All view so the existing single-number `logged` stays authoritative.
  const [bhiLogged, setBhiLogged] = useState({});
  // All-lens multi-program rows: which family the quick-log chips are scoped
  // to, per patient (tap a program line to switch). Absent → behind-most.
  const [logFamilyByPid, setLogFamilyByPid] = useState({});
  const [recent, setRecent] = useState([]); // [{id, pid, presetId, label, mins, ts}]
  const [editingPresets, setEditingPresets] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);
  // Review queue for untagged behavioral time (finding: untagged entries
  // default to the CCM family and silently miss 99484).
  const [bhRetagOpen, setBhRetagOpen] = useState(false);
  // Patient whose BHI assessment modal is open (null = closed). Opened by
  // tapping the assessment chip in the BHI-view row.
  const [assessmentFor, setAssessmentFor] = useState(null);
  // Pain assessments use a parent-owned PUT so raw.pain.assessments and the
  // pain-tagged activity entry are persisted atomically.
  const [painAssessmentFor, setPainAssessmentFor] = useState(null);
  const [settings, setSettings] = useState({});
  const [scanning, setScanning] = useState(false);
  const [suggestions, setSuggestions] = useState([]); // [{pid, preset, why, need, confidence}]
  // Model used by the AI scan modal's LLM-backed note classifier. Default
  // Haiku (cost/speed); user can switch to Sonnet in the modal header for
  // better recall on paraphrasing. Choice persists for the session only.
  const [scanModel, setScanModel] = useState('bedrock-haiku');
  const [toast, setToast] = useState(null);
  const [flashing, setFlashing] = useState(null); // {pid, mins}
  // Patient whose entries modal is open (null = closed). Opened by the row's
  // "›" button — replaces the legacy "jump to Studio" behavior so users can
  // edit/delete entries without leaving Time Logs.
  const [entriesFor, setEntriesFor] = useState(null);
  // All lab reports across the practice (single bulk fetch), grouped by
  // patient id at render time. Drives the Lab-review applicability marker
  // and the "Review labs (N)" AI custom chip. Null until the fetch resolves
  // — components fall back to "no opinion" while loading.
  const [allLabReports, setAllLabReports] = useState(null);
  // Phone notes (Elation non_visit_notes type:'phone') for the active month,
  // keyed by elation_id. Drives the AI text-signal scan (e.g., a phone note
  // mentioning "family" → Caregiver applies). Null until fetch resolves; an
  // elation_id absent from the map is treated as "no notes / no opinion".
  const [phoneNotesByEid, setPhoneNotesByEid] = useState(null);
  // Per-row refs so we can scroll to a deep-linked patient when the Push-to-EMR
  // modal fires "Log time →".
  const rowRefs = useRef({});
  // Briefly applied class on the focused row.
  const [highlightedId, setHighlightedId] = useState(null);

  // Power-user: `/` focuses the search input (skips when already typing in a field).
  const searchInputRef = useRef(null);
  useEffect(() => {
    const handler = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      if (e.key === '/') {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const s = await ccmApi.getSettings();
        if (!active) return;
        setSettings(s || {});
        if (s && Array.isArray(s.quick_log_presets) && s.quick_log_presets.length) {
          setPresets(normalizePresets(s.quick_log_presets));
        }
        if (s && Array.isArray(s.bhi_quick_log_presets) && s.bhi_quick_log_presets.length) {
          setBhiPresets(normalizeBhiPresets(s.bhi_quick_log_presets));
        }
      } catch (_) {
        // Keep built-ins if settings are unavailable; logging time must remain usable.
      }
    })();
    return () => { active = false; };
  }, []);

  async function savePresets(nextList) {
    const normalized = normalizePresets(nextList);
    await ccmApi.saveSettings({ quick_log_presets: presetsForSettings(normalized) });
    setPresets(normalized);
    const id = 'presets' + Date.now();
    setToast({ id, text: 'Activity presets saved practice-wide', entryId: null });
    setTimeout(() => setToast(t => (t && t.id === id ? null : t)), 3200);
  }

  async function saveBhiPresets(nextList) {
    const normalized = normalizeBhiPresets(nextList);
    await ccmApi.saveSettings({ bhi_quick_log_presets: bhiPresetsForSettings(normalized) });
    setBhiPresets(normalized);
    const id = 'bhipresets' + Date.now();
    setToast({ id, text: 'BH activity presets saved practice-wide', entryId: null });
    setTimeout(() => setToast(t => (t && t.id === id ? null : t)), 3200);
  }

  // Load patients once on mount; reload if month changes (logged-this-month is derived from month).
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    ccmApi.listPatients().then((rows) => {
      if (cancelled) return;
      const adapted = (rows || []).map(r => adaptPatient(r, month)).filter(Boolean);
      setPatients(adapted);
      // Seed the local 'logged' map from the loaded patient totals.
      setLogged(Object.fromEntries(adapted.map(p => [p.id, p.logged])));
      setLoading(false);
    }).catch(() => {
      if (cancelled) return;
      setPatients([]);
      setLogged({});
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [month]);

  // When the chrome programFilter changes (CCM ↔ BHI ↔ All), the cached per-patient
  // minute totals reflect whichever family was last computed. Re-seed the
  // `logged` map from the current month + filter so progress numbers are honest.
  // bhiLogged is always seeded from the BHI minutes so the All-lens split display
  // (CCM Xm · BHI Ym) stays consistent across filter switches.
  // Per-patient CPM evidence for the pain lens, memoized — the seeding effect
  // and every row render share ONE computation so ordinary rerenders (typing
  // in search, flash/toast timeouts) don't rescan each activity log
  // (codex 8d review). null outside the pain lens.
  const painEvByPid = useMemo(() => {
    if (programFilter !== 'pain') return null;
    const out = {};
    for (const p of patients) out[p.id] = cpmMonthlyEvidence(p._raw, month, staffList);
    return out;
  }, [programFilter, patients, month, staffList]);

  // Pain-lens minutes for a raw blob under the current counting mode. Used by
  // the optimistic add/undo paths, which pass a hypothetical raw (entry
  // appended/removed) — adding raw preset minutes would move the bar for
  // staff/other-provider time that can never bill (codex 8d review).
  function painLensMinutes(rawData) {
    return cpmMonthlyEvidence(rawData, month, staffList).billableProviderMinutes;
  }

  useEffect(() => {
    if (loading) return;
    setLogged(Object.fromEntries(patients.map(p => {
      const ccm = ccmActivityMinutes(p._raw, month);
      const bhi = bhiActivityMinutes(p._raw, month);
      // Pain lens shows the minutes that can actually BILL: only the
      // designated CPM billing provider's personally-furnished minutes count
      // (enforce-from-day-one) — the row chip surfaces the excluded remainder.
      const mins = programFilter === 'bhi' ? bhi
              : programFilter === 'pain' ? (() => {
                  const ev = (painEvByPid && painEvByPid[p.id]) || cpmMonthlyEvidence(p._raw, month, staffList);
                  return ev.billableProviderMinutes;
                })()
              // The All-lens aggregate deliberately excludes both Pain and
              // PIN; their program lines compute family minutes directly.
              : programFilter === 'all' ? (ccm + bhi)
              : ccm;
      return [p.id, mins];
    })));
    setBhiLogged(Object.fromEntries(patients.map(p => [p.id, bhiActivityMinutes(p._raw, month)])));
  }, [programFilter, patients, month, loading, staffList, painEvByPid]);

  // Program-line selection is an all-lens concept — drop it on lens change.
  useEffect(() => { setLogFamilyByPid({}); }, [programFilter]);

  // Month-pacing frame for the "At risk" lens: current month → today's date;
  // a CLOSED month → its last day (projection collapses to "ended below
  // target"); a future month → day 1 (everything projects fine until real).
  const { dayOfMonth, daysInMonth } = useMemo(() => {
    const [y, m] = String(month || '').split('-').map(Number);
    const days = y && m ? new Date(y, m, 0).getDate() : 31;
    const now = new Date();
    const curKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    const day = month === curKey ? now.getDate() : (month < curKey ? days : 1);
    return { dayOfMonth: day, daysInMonth: days };
  }, [month]);

  // All-lens per-program slots for a row: one line per enrolled family with a
  // minute target. CCM minutes = combined logged minus BHI (the all-lens
  // `logged` basis is ccm+bhi); PIN and pain are computed directly (never in
  // `logged`).
  function programSlotsFor(p) {
    const combined = logged[p.id] ?? 0;
    const bhiMins = bhiLogged[p.id] || 0;
    const slots = [];
    if ((p.targets?.ccm || 0) > 0) {
      slots.push({ family: 'ccm', label: 'CCM', mins: Math.max(0, combined - bhiMins), target: p.targets.ccm });
    }
    // BHI slot only for BHI-ENROLLED patients: targets.bhi is positive for
    // EVERYONE (resolveBhiBillCode picks WHICH code — 99484 vs G0570 — not
    // whether the patient is enrolled), so the target alone must not put a
    // BHI line on every all-lens row. Enrollment lives at billing.bhiProgram.
    const bhiProgram = String(p._raw?.billing?.bhiProgram || '').trim().toLowerCase();
    if (bhiProgram && bhiProgram !== 'none' && (p.targets?.bhi || 0) > 0) {
      slots.push({ family: 'bhi', label: 'BHI', mins: bhiMins, target: p.targets.bhi });
    }
    if (pinEnabled && isPinEnrolledPatient(p._raw)) {
      slots.push({
        family: 'pin', label: 'Nav',
        mins: pinMonthlyMinutes(p._raw, month),
        target: NAV_BASE_TARGET_MIN,
      });
    }
    // Pain surfaces are provider/admin-scoped (P0.0 canSeePain): a staff
    // session must never see the Pain line or pain presets, even for a
    // pain-enrolled patient in the All lens. Minutes shown are the designated
    // provider's billable minutes — the same basis the bill resolves from.
    if (painEnabled && (p.targets?.pain || 0) > 0) {
      slots.push({
        family: 'pain', label: 'Pain',
        mins: cpmMonthlyEvidence(p._raw, month, staffList).billableProviderMinutes,
        target: p.targets.pain,
      });
    }
    return slots;
  }

  // Chips scope for a multi-slot row: the user's sticky per-row pick when
  // still valid, else the behind-most family (lowest fraction of target among
  // unmet slots) — so the default tap is almost always the right bucket.
  function selectedFamilyFor(p, slots) {
    const chosen = logFamilyByPid[p.id];
    if (chosen && slots.some(s => s.family === chosen)) return chosen;
    const unmet = slots.filter(s => s.mins < s.target);
    const pool = unmet.length ? unmet : slots;
    return pool.reduce((best, s) => ((s.mins / s.target) < (best.mins / best.target) ? s : best), pool[0]).family;
  }

  // One program line's bar — the CCM ladder keeps its milestone ticks; other
  // families get the simple red/yellow/green target bar.
  function programLineBar(slot, raw) {
    if (slot.family === 'ccm' && isCcmTierPatient(raw)) {
      const prog = ccmMilestoneProgress(slot.mins);
      const fill = prog.color === 'green' ? 'var(--ok)'
        : prog.color === 'yellow' ? 'var(--warn)' : 'var(--bad)';
      return (
        <div className="tinybar" style={{ position: 'relative', overflow: 'visible', marginTop: 0 }}
             title={`${prog.mins} CCM min — ticks: 20 (99490) · 40/60 (99439 add-ons) · 90 (99487/99491 bump)`}>
          <div style={{
            width: `${prog.pct}%`, background: fill, borderRadius: 'inherit',
            transition: 'width 350ms cubic-bezier(0.16, 1, 0.3, 1)',
          }}/>
          {CCM_MILESTONES.map(m => (
            <span key={m.min} title={m.title} style={{
              position: 'absolute', top: -1, bottom: -1, width: 2,
              left: `calc(${(m.min / 90) * 100}% - 1px)`,
              background: prog.mins >= m.min ? 'var(--ok)' : 'var(--ink-4)',
              borderRadius: 1, opacity: 0.9,
            }}/>
          ))}
        </div>
      );
    }
    const c = simpleTargetColor(slot.mins, slot.target);
    return (
      <div className="tinybar" style={{ marginTop: 0 }}>
        <div style={{
          width: `${Math.min(100, (slot.mins / slot.target) * 100)}%`,
          background: c === 'green' ? 'var(--ok)' : c === 'yellow' ? 'var(--warn)' : 'var(--bad)',
          transition: 'width 350ms cubic-bezier(0.16, 1, 0.3, 1)',
        }}/>
      </div>
    );
  }

  // Minute-target slots the pacing math runs over — per-family in the all
  // lens, the single lens slot otherwise.
  function paceSlotsFor(p) {
    if (programFilter === 'all') return programSlotsFor(p);
    const t = targetForRow(p, programFilter);
    if (t <= 0) return [];
    const family = programFilter === 'bhi' ? 'bhi' : programFilter === 'pain' ? 'pain' : 'ccm';
    return [{ family, mins: logged[p.id] ?? p.logged ?? 0, target: t }];
  }

  // Close the AI scan modal (and clear suggestions) whenever the user switches
  // to BHI view — the scan pipeline builds CCM-shaped suggestions only.
  useEffect(() => {
    if ((programFilter === 'bhi' || programFilter === 'pain') && scanOpen) {
      setScanOpen(false);
      setSuggestions([]);
    }
  }, [programFilter, scanOpen]);

  // Bulk-fetch lab reports once on mount. One GET /data/lab-reports (limit 500)
  // is cheaper than per-row fetches and is enough to drive applicability
  // markers + the "Review labs (N)" custom chip across the visible roster.
  // Failures are non-fatal — the lab signal just stays "no opinion".
  useEffect(() => {
    let cancelled = false;
    api.get('/data/lab-reports?limit=500').then((rows) => {
      if (cancelled) return;
      setAllLabReports(Array.isArray(rows) ? rows : []);
    }).catch(() => {
      if (cancelled) return;
      setAllLabReports([]);
    });
    return () => { cancelled = true; };
  }, []);

  // Bucket lab reports by patient_id × month so per-row lookup is O(1). We
  // re-bucket whenever the active month or the underlying lab list changes.
  const labsByPid = useMemo(() => {
    const out = {};
    if (!Array.isArray(allLabReports) || !month) return out;
    for (const r of allLabReports) {
      if (!r || !r.patient_id) continue;
      const d = r.report_date && typeof r.report_date === 'string' ? r.report_date.slice(0, 7) : '';
      if (d !== month) continue;
      out[r.patient_id] = (out[r.patient_id] || 0) + 1;
    }
    return out;
  }, [allLabReports, month]);

  // Bulk-fetch phone notes for tagged patients with an elation_id. We refetch
  // when the active month changes (Elation filters by document_date inside
  // the helper). Cancellation guards prevent stale state on rapid month flips.
  useEffect(() => {
    let cancelled = false;
    if (!patients || !patients.length) {
      setPhoneNotesByEid({});
      return () => {};
    }
    const eids = patients
      .map(p => p._raw && p._raw.elation_id ? String(p._raw.elation_id) : null)
      .filter(Boolean);
    if (!eids.length) {
      setPhoneNotesByEid({});
      return () => {};
    }
    setPhoneNotesByEid(null);
    ccmApi.phoneNotesInMonth(eids, month).then((resp) => {
      if (cancelled) return;
      setPhoneNotesByEid((resp && resp.results) || {});
    }).catch(() => {
      if (cancelled) return;
      setPhoneNotesByEid({});
    });
    return () => { cancelled = true; };
  }, [patients, month]);

  // Deep-link from the Push-to-EMR modal: when focusPatient changes, scroll
  // the targeted row into view and apply a brief highlight class. We force
  // the filter to 'all' so the row is visible even if its program/status
  // would otherwise hide it under the current filter combo. The `ts` field
  // on focusPatient ensures repeated taps on the same patient retrigger the
  // effect.
  useEffect(() => {
    if (!focusPatient?.id || loading) return;
    // Force visibility: 'all' filters always include the patient.
    setStatusFilter('all');
    setQuery('');
    // Wait for layout to settle after filter changes before scrolling.
    const tid = window.setTimeout(() => {
      const node = rowRefs.current[focusPatient.id];
      if (node && typeof node.scrollIntoView === 'function') {
        node.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      setHighlightedId(focusPatient.id);
      onFocusConsumed?.();
      window.setTimeout(() => setHighlightedId((cur) => (cur === focusPatient.id ? null : cur)), 1800);
    }, 50);
    return () => window.clearTimeout(tid);
  }, [focusPatient?.id, focusPatient?.ts, loading, onFocusConsumed]);

  // Operational roster = patients who can be billed for time this month —
  // active, consent not declined, not explicitly workflow-excluded by Elation
  // sync (no_patient_tags + tagsVerified), and enrolled in a program.
  // Untagged patients without an explicit exclusion stay visible — v2 only
  // hides them when Elation eligibility marks them so. See lib/workflow.js
  // for the predicate definitions, which mirror v2's ccm-compliance.js.
  const operationalPatients = useMemo(
    () => patients.filter(p => operationalForFilter(p, programFilter)),
    [patients, programFilter]
  );

  // The runway lenses only exist on the CCM ladder — leaving the ccm/all lens
  // with one active would silently behave like 'all', so reset instead.
  useEffect(() => {
    if ((statusFilter === 'addon' || statusFilter === 'complex')
        && programFilter !== 'ccm' && programFilter !== 'all') {
      setStatusFilter('all');
    }
  }, [programFilter, statusFilter]);

  // Per-patient pace over the operational roster: status ('complete' /
  // 'on-pace' / 'at-risk' / 'untouched') + urgency (minutes owed ÷ days left)
  // for the At-risk lens ordering. null = no minute-target this month (APCM).
  const paceByPid = useMemo(() => {
    const map = new Map();
    for (const p of operationalPatients) {
      map.set(p.id, rowPace(paceSlotsFor(p), dayOfMonth, daysInMonth));
    }
    return map;
  }, [operationalPatients, logged, bhiLogged, month, programFilter, dayOfMonth, daysInMonth]);

  const paceCounts = useMemo(() => {
    const counts = { complete: 0, onPace: 0, atRisk: 0, untouched: 0 };
    for (const pace of paceByPid.values()) {
      if (!pace) continue;
      if (pace.status === 'complete') counts.complete++;
      else if (pace.status === 'on-pace') counts.onPace++;
      else if (pace.status === 'at-risk') counts.atRisk++;
      else counts.untouched++;
    }
    return counts;
  }, [paceByPid]);

  const list = useMemo(() => operationalPatients.filter(p => {
    if (hideNoChronic && chronicCount(p) === 0) return false;
    if (query) {
      const q = query.toLowerCase();
      if (!p.name.toLowerCase().includes(q) && !(p.mrn || '').toLowerCase().includes(q)) return false;
    }
    if (tagFilter !== 'all' && !p.tags.includes(tagFilter)) return false;
    const mins = logged[p.id] ?? p.logged;
    const slotTarget = targetForRow(p, programFilter);
    if (statusFilter === 'below' && (slotTarget === 0 || mins >= slotTarget)) return false;
    if (statusFilter === 'met'   && (slotTarget === 0 || mins < slotTarget))  return false;
    // Runway lenses (ccm/all lens only): 'addon' = base met with a 99439 unit
    // still reachable; 'complex' = climbing 60→90 or ≥90 with the tier bump
    // not yet affirmed. CCM minutes only — the all lens strips BHI minutes.
    if ((statusFilter === 'addon' || statusFilter === 'complex')
        && (programFilter === 'ccm' || programFilter === 'all')) {
      const ccmMins = programFilter === 'all' ? mins - (bhiLogged[p.id] || 0) : mins;
      if (ccmRunwayLens(p._raw, month, ccmMins) !== statusFilter) return false;
    }
    // At-risk lens: pacing projection says this patient won't reach a target
    // by month-end at the current logging rate.
    if (statusFilter === 'atrisk' && paceByPid.get(p.id)?.status !== 'at-risk') return false;
    return true;
  }).sort((a, b) => {
    // Only the At-risk lens reorders: hardest-to-save first (minutes owed ÷
    // days left). Every other lens keeps the roster's stable order.
    if (statusFilter !== 'atrisk') return 0;
    return (paceByPid.get(b.id)?.urgency || 0) - (paceByPid.get(a.id)?.urgency || 0);
  }), [operationalPatients, query, statusFilter, programFilter, tagFilter, logged, bhiLogged, month, hideNoChronic, paceByPid]);

  // Unique Elation tags across the operational roster (sorted, lower-cased
  // values — display capitalization is applied per-chip at render time).
  const allTags = useMemo(() => {
    const set = new Set();
    for (const p of operationalPatients) for (const t of p.tags) set.add(t);
    return Array.from(set).sort();
  }, [operationalPatients]);

  // Derived summary numbers from the operational (tagged) roster only — keeps
  // the "of N" totals consistent with what's actually visible in the table.
  const { metCount, belowCount, addonCount, complexCount } = useMemo(() => {
    let met = 0, below = 0, addon = 0, complex = 0;
    for (const p of operationalPatients) {
      const slotTarget = targetForRow(p, programFilter);
      if (slotTarget === 0) continue;
      const mins = logged[p.id] ?? p.logged;
      if (mins >= slotTarget) met++;
      else below++;
      if (programFilter === 'ccm' || programFilter === 'all') {
        const ccmMins = programFilter === 'all' ? mins - (bhiLogged[p.id] || 0) : mins;
        const lens = ccmRunwayLens(p._raw, month, ccmMins);
        if (lens === 'addon') addon++;
        else if (lens === 'complex') complex++;
      }
    }
    return { metCount: met, belowCount: below, addonCount: addon, complexCount: complex };
  }, [operationalPatients, logged, bhiLogged, month, programFilter]);

  // In the All lens we also compute per-program counts for the split stat
  // display. Membership is per-program ENROLLMENT (operationalForFilter), not
  // target > 0, and each program is measured against its own minutes — see
  // lib/all-lens-metrics for why both rules matter.
  const allMetCounts = useMemo(
    () => programFilter === 'all'
      ? metCountsByProgram(operationalPatients, logged, bhiLogged, targetForRow, operationalForFilter)
      : null,
    [programFilter, operationalPatients, logged, bhiLogged]
  );

  const slotTotal = operationalPatients.filter(p => targetForRow(p, programFilter) > 0).length;

  // AI scan: builds suggestions for every CCM patient in the current filtered
  // view (target > 0). Below-target patients show the "Xm short" badge;
  // already-met patients still get suggestions so a clinician can decide to
  // log additional time when there's appropriate activity to bill. Unlike
  // the per-row chips (synchronous, patient-blob only), the modal fetches
  // labs + latest visit notes in parallel so the heuristics see this-month
  // signals (lab results, "specialist"/"consult" in the most recent
  // encounter). Confidence is an advisory band derived from signal source
  // (rule engine match + LLM cross-check).
  // Suggestions reveal with a staggered setTimeout for visual rhythm once
  // the data fetch resolves.
  // `modelOverride` lets the ScanSuggest model picker re-run immediately
  // with the newly selected model — avoids waiting for setScanModel to
  // flush before the next runScan reads it.
  async function runScan(modelOverride) {
    const useModel = modelOverride || scanModel;
    setScanOpen(true);
    setScanning(true);
    setSuggestions([]);
    const candidates = list.filter(p => p.target > 0);
    if (candidates.length === 0) {
      setScanning(false);
      return;
    }

    // Parallel lab fetch — one GET per candidate. Errors are squashed to []
    // so a single bad row doesn't abort the whole scan.
    const labReportsByPid = {};
    await Promise.all(candidates.map(async (p) => {
      try {
        const reports = await api.get(`/data/lab-reports/${encodeURIComponent(p.id)}`);
        labReportsByPid[p.id] = Array.isArray(reports) ? reports : [];
      } catch (_) {
        labReportsByPid[p.id] = [];
      }
    }));

    // Latest visit notes — one bulk call across all candidates with an
    // elation_id. Skipped entirely when no one's linked to Elation. Errors
    // squashed to empty so the modal still shows the lab + blob-based signals.
    const elationIds = candidates
      .map(p => p._raw && p._raw.elation_id ? String(p._raw.elation_id) : null)
      .filter(Boolean);
    let latestNotesByEid = {};
    if (elationIds.length) {
      try {
        const resp = await ccmApi.latestVisitNotes(elationIds);
        latestNotesByEid = (resp && resp.results) || {};
      } catch (_) {
        latestNotesByEid = {};
      }
    }

    // LLM-backed note classifier — combines latest visit-note text + phone
    // notes per patient and asks the selected model which CCM categories
    // each note evidences. Errors squashed: on failure we fall back to the
    // regex / structured-data signals already in the engine.
    const notesToClassify = [];
    candidates.forEach((p) => {
      const eid = p._raw && p._raw.elation_id ? String(p._raw.elation_id) : null;
      const phoneNotes = (eid && phoneNotesByEid && phoneNotesByEid[eid]) || [];
      phoneNotes.forEach((n, i) => {
        if (n && n.text) {
          notesToClassify.push({
            id: `${p.id}:pn:${i}`,
            text: String(n.text).slice(0, 4000),
            date: n.date || '',
          });
        }
      });
      const vn = eid ? latestNotesByEid[eid] : null;
      if (vn && vn.text) {
        notesToClassify.push({
          id: `${p.id}:vn`,
          text: String(vn.text).slice(0, 4000),
          date: vn.document_date || '',
        });
      }
    });
    // Chunk to the endpoint's 50-note-per-call cap.
    const llmCategoriesByPid = {};
    if (notesToClassify.length) {
      const CHUNK = 50;
      for (let i = 0; i < notesToClassify.length; i += CHUNK) {
        const chunk = notesToClassify.slice(i, i + CHUNK);
        try {
          const resp = await ccmApi.classifyNotes(useModel, chunk);
          const results = (resp && resp.results) || [];
          for (const r of results) {
            if (!r || typeof r.id !== 'string') continue;
            const pid = r.id.split(':')[0];
            if (!pid) continue;
            if (!llmCategoriesByPid[pid]) llmCategoriesByPid[pid] = new Set();
            for (const c of (r.categories || [])) llmCategoriesByPid[pid].add(c);
          }
        } catch (_) {
          // continue without LLM signal for this chunk
        }
      }
    }

    // Build the suggestions array. Per patient, emit multiple ranked options
    // (engine caps at 3) so a clinician has enough choices to fill toward the
    // 60-min non-complex-CCM ceiling (99490 + 99439 + 99439). LLM categories
    // augment the rule-based list as extra suggestions (with "AI" provenance
    // in the why string) when the engine missed them.
    const CCM_MONTHLY_CEILING = 60;
    const built = [];
    candidates.forEach((p) => {
      const currentLogged = logged[p.id] ?? 0;
      const need = p.target - currentLogged;
      const eid = p._raw && p._raw.elation_id ? String(p._raw.elation_id) : null;
      const latestNote = eid ? latestNotesByEid[eid] : null;
      const context = {
        labReports: labReportsByPid[p.id] || [],
        latestVisitNote: latestNote
          ? { text: latestNote.text || '', date: latestNote.document_date || '' }
          : null,
      };
      const ranked = suggestForPatientWithContext(p, effectivePresets, context, { monthKey: month });

      // LLM-only augmentation: any category the model flagged that the rule
      // engine didn't already cover gets appended as a lower-priority entry.
      const ruleLabels = new Set(ranked.map(r => r.preset && r.preset.label).filter(Boolean));
      const llmCats = llmCategoriesByPid[p.id] || new Set();
      for (const cat of llmCats) {
        if (ruleLabels.has(cat)) continue;
        const preset = (effectivePresets || []).find(pr => pr && pr.label === cat);
        if (!preset) continue;
        ranked.push({ preset, why: `AI (${useModel === 'bedrock-sonnet' ? 'Sonnet' : 'Haiku'}): note text suggests "${cat}"`, _llm: true });
      }
      if (!ranked.length) return;

      let cumulative = 0;
      const gap = Math.max(CCM_MONTHLY_CEILING - currentLogged, 0);
      const hasContext =
        (context.labReports && context.labReports.length > 0) ||
        !!(context.latestVisitNote && context.latestVisitNote.text);
      for (let i = 0; i < ranked.length; i++) {
        const item = ranked[i];
        // "llmConfirmed" = the rule engine produced this entry AND the LLM
        // independently flagged the same category. LLM-only entries (_llm)
        // don't qualify — they're a Medium signal, not a corroborated High.
        const llmConfirmed = !item._llm && llmCats.has(item.preset && item.preset.label);
        const confidence = classifyConfidence({
          _llm: item._llm,
          llmConfirmed,
          hasContext,
        });
        built.push({
          // Unique per suggestion (not per patient) so Skip/Apply targets one
          // row at a time even when a patient has several stacked suggestions.
          sid: `${p.id}:${item.preset?.id || i}`,
          pid: p.id,
          preset: item.preset,
          why: item.why,
          need,
          confidence,
        });
        cumulative += Number(item.preset?.mins) || 0;
        // Stop once we've offered enough to fill the gap to the 60-min ceiling.
        // For already-over-60 patients, gap is 0 — show one suggestion only.
        if (cumulative >= gap) break;
      }
    });

    built.forEach((s, i) => {
      setTimeout(() => {
        setSuggestions(prev => [...prev, s]);
        if (i === built.length - 1) setScanning(false);
      }, 220 + i * 140);
    });
    if (built.length === 0) setScanning(false);
  }

  // Refresh the full patient list after a 409 optimistic-lock conflict. Bulk
  // GET is the only available shape (api.js intentionally has no per-patient
  // GET, and Task 3 forbids modifying it). Cancels gracefully if the page
  // unmounted mid-refresh by tolerating an empty/missing response.
  async function refetchPatients() {
    try {
      const rows = await ccmApi.listPatients();
      const adapted = (rows || []).map(r => adaptPatient(r, month)).filter(Boolean);
      setPatients(adapted);
      // Reseed logged totals from the fresh data so Apply All retry sees
      // post-conflict numbers.
      setLogged(Object.fromEntries(adapted.map(p => [p.id, p.logged])));
    } catch (_) {
      // Squash refetch errors — UI keeps last-known state and user can refresh.
    }
  }

  // Untagged behavioral time across the loaded roster. Computed over ALL
  // patients, not the lens slice — the point is finding BHI time hiding in
  // the CCM family, which by definition doesn't show under the BHI lens.
  const bhRetagCandidates = useMemo(() => {
    if (!bhiEnabled) return [];
    const out = [];
    for (const p of patients) {
      if (!p || !p._raw) continue;
      for (const c of untaggedBhCandidates(p._raw)) {
        out.push({ pid: p.id, patientName: p.name, ...c });
      }
    }
    return out;
  }, [bhiEnabled, patients]);

  // Retag one reviewed entry to the patient's BHI code. Full-blob PUT-back
  // with the optimistic-lock timestamp (mirrors addTime); audited so the
  // reclassification is traceable per entry.
  async function retagBhEntry(pid, entryId) {
    const patient = patients.find(x => x.id === pid);
    if (!patient || !patient._raw) return false;
    const nextData = retagEntryToBhi(patient._raw, entryId);
    if (!nextData) return false;
    try {
      const resp = await ccmApi.updatePatient(pid, nextData);
      const fresh = resp && resp.updatedAt ? resp.updatedAt : null;
      setPatients(prev => prev.map(x => x.id === pid
        ? { ...x, _raw: { ...nextData, _updatedAt: fresh || nextData._updatedAt } }
        : x));
      try {
        ccmApi.auditEvent('BHI_RETAG_ENTRY', 'patients', pid,
          `entry=${entryId} untagged -> ${(patient._raw.billing || {}).bhiProgram || ''}`);
      } catch (_) { /* audit is best-effort */ }
      return true;
    } catch (e) {
      if (e && e.status === 409) refetchPatients();
      throw e;
    }
  }

  // Build a persisted activity-log entry from a preset. Shared between
  // addTime (single) and addTimeBatch (bulk Apply All).
  // Logging user's credential, resolved from the staff list by stable id
  // (name fallback). Stamped on every entry so CPM/tier billing can verify
  // WHO furnished the time without brittle post-hoc name matching
  // (CPM-BILLING-COMPLIANCE-PLAN.md P0.1).
  const [myCredential, setMyCredential] = useState(null);
  useEffect(() => {
    let live = true;
    ccmApi.listStaff().then((list) => {
      if (!live || !Array.isArray(list)) return;
      setStaffList(list); // shared with the pain lens (cpmMonthlyEvidence)
      const byId = list.find((st) => st && currentUser?.id && String(st.id) === String(currentUser.id));
      const uname = staffNameForUser(currentUser).toLowerCase();
      const byName = uname ? list.filter((st) => String((st && st.name) || '').trim().toLowerCase() === uname) : [];
      // Stable-id match first; a name match only when UNIQUE, and its weaker
      // provenance is recorded on every entry it stamps (user decision
      // 2026-07-20 — mirror the truthful-provenance convention).
      const me = byId || (byName.length === 1 ? byName[0] : null);
      if (me && me.credential) {
        setMyCredential({ credential: me.credential, source: byId ? 'staff-list-id' : 'staff-list-name' });
      }
    }).catch(() => {});
    return () => { live = false; };
  }, [currentUser?.id]);

  function buildEntry(pid, preset) {
    const tsMs = Date.now() + Math.floor(Math.random() * 1000); // jitter so batched ids don't collide on a single tick
    const id = 'r' + tsMs + Math.random().toString(36).slice(2, 6);
    const staff = staffNameForUser(currentUser);
    const note = presetActivityNote(preset);
    const persisted = {
      id,
      pid,
      // AI chips set `presetId` to the underlying category preset so analytics
      // can still group by activity type; their chip-level `id` is unique per
      // chip and not a preset reference.
      presetId: preset.presetId || preset.id,
      label: preset.label,
      type: preset.type || preset.label || 'Other',
      duration: preset.mins,
      ts: tsMs,
      date: entryDateForMonth(month),
      kind: preset.kind || 'manual',
      desc: note || preset.desc || preset.label,
      notes: note,
    };
    if (isDeliveryMode(preset.mode)) persisted.mode = preset.mode;
    // Derive the entry's billing CPT from the preset's programFamily, not the
    // chrome lens. cptForPreset() reads `patient._raw.billing.bhiProgram` for
    // BHI-family presets, `billing.navProgram` for PIN, and the matching family
    // slot otherwise. This makes
    // the All lens correct: a BHI preset logged in All view tags entry.program
    // with the patient's BHI CPT, not their CCM CPT. Patients with no billing
    // slot for the preset's family get billingProgram='' — entry stays untagged,
    // matching the legacy behavior for not-yet-onboarded patients.
    const patient = patients.find(x => x.id === pid);
    const billingProgram = cptForPreset(preset, patient);
    if (billingProgram) persisted.program = billingProgram;
    if (staff) persisted.staff = staff;
    if (currentUser?.id) persisted.staffUserId = currentUser.id;
    if (myCredential) { persisted.credential = myCredential.credential; persisted.credentialSource = myCredential.source; }
    return persisted;
  }

  // Apply one suggestion: logs the time via addTime (which is async + optimistic)
  // and drops the row from the suggestion list immediately. We don't await the
  // PUT here — addTime handles rollback + error toast internally. Uses `sid`
  // (per-suggestion id) so stacked rows for the same patient don't all drop.
  function applySuggestion(s) {
    addTime(s.pid, s.preset);
    setSuggestions(prev => prev.filter(x => x.sid !== s.sid));
  }

  // Apply every suggestion. Group by patient so same-patient suggestions land
  // in a single PUT (otherwise the closure-read `patients` snapshot causes
  // each per-suggestion addTime to clobber the previous append — that's the
  // data-loss bug this task fixes). Cross-patient PUTs run in parallel.
  function applyAll() {
    const byPid = new Map();
    for (const s of suggestions) {
      if (!byPid.has(s.pid)) byPid.set(s.pid, []);
      byPid.get(s.pid).push(s.preset);
    }
    setSuggestions([]);
    // Fire-and-forget — addTimeBatch handles its own optimistic UI, rollback,
    // toast, and 409 refetch. We don't await so the modal can close promptly.
    for (const [pid, presets] of byPid.entries()) {
      addTimeBatch(pid, presets);
    }
    setTimeout(() => setScanOpen(false), 200);
  }

  // addTime — optimistic UI update with backend persistence + rollback on failure.
  // Server contract (server/routes/patients.js): PUT /data/patients/:id stores the
  // request body directly as the patient data blob, so we must send the FULL data
  // (with the new activityLog entry appended), not just a partial patch.
  //
  // _updatedAt: we now thread the cached value through the PUT body so the
  // server's optimistic-lock check (server/routes/patients.js:122-141) can
  // fire on stale writes. Success response is {ok, updatedAt}; we capture it
  // into local state so subsequent PUTs carry the freshest timestamp.
  async function addTime(pid, preset) {
    const persistedEntry = buildEntry(pid, preset);
    const id = persistedEntry.id;
    // In-memory `recent` keeps `mins` so existing UI render paths (r.mins) and
    // rollback in undoEntry (entry.mins) continue to work without churn.
    const recentEntry = { ...persistedEntry, mins: preset.mins };

    // Optimistic UI updates — keep existing flash/toast/recent behavior.
    // Pain lens: derive the honest evidence-based total from the would-be log
    // instead of adding raw minutes (a non-designated entry must not move the
    // bar it can never fill).
    const p = patients.find(x => x.id === pid);
    setLogged(prev => {
      if (programFilter === 'pain' && p && p._raw) {
        return { ...prev, [pid]: painLensMinutes({ ...p._raw, activityLog: [...(p._raw.activityLog || []), persistedEntry] }) };
      }
      return { ...prev, [pid]: (prev[pid] ?? 0) + preset.mins };
    });
    if (preset.programFamily === 'bhi') {
      setBhiLogged(prev => ({ ...prev, [pid]: (prev[pid] ?? 0) + preset.mins }));
    }
    setRecent(prev => [recentEntry, ...prev].slice(0, 14));
    setFlashing({ pid, mins: preset.mins });
    setTimeout(() => setFlashing(null), 750);
    setToast({ id, text: `+${preset.mins}m ${preset.label} → ${p ? p.name : 'patient'}`, entryId: id });
    setTimeout(() => setToast(t => (t && t.id === id ? null : t)), 3200);

    // Persist to backend. Server stores body as the full data blob, so send the
    // full patient data with the new activityLog entry appended. We keep
    // _updatedAt on the body so the server's 409 optimistic-lock fires on
    // stale writes; the server strips _updatedAt before storing.
    try {
      const patient = patients.find(x => x.id === pid);
      if (!patient || !patient._raw) throw new Error('patient data not loaded');
      const nextData = {
        ...patient._raw,
        activityLog: [...(patient._raw.activityLog || []), persistedEntry],
      };
      const resp = await ccmApi.updatePatient(pid, nextData);
      const fresh = resp && resp.updatedAt ? resp.updatedAt : null;
      // Keep local patients state in sync so subsequent ops see the latest
      // data AND the latest _updatedAt for optimistic locking.
      setPatients(prev => prev.map(x => x.id === pid
        ? { ...x, _raw: { ...nextData, _updatedAt: fresh || nextData._updatedAt } }
        : x));
    } catch (e) {
      // Roll back optimistic state. 409 → soft conflict (refetch + retry toast);
      // anything else → hard failure toast. Pain lens: reset to the evidence
      // total of the UNCHANGED raw (the PUT failed) — subtracting raw minutes
      // could undercount if a reseed landed mid-flight.
      setLogged(prev => {
        if (programFilter === 'pain' && p && p._raw) {
          return { ...prev, [pid]: painLensMinutes(p._raw) };
        }
        return { ...prev, [pid]: Math.max(0, (prev[pid] ?? 0) - preset.mins) };
      });
      if (preset.programFamily === 'bhi') {
        setBhiLogged(prev => ({ ...prev, [pid]: Math.max(0, (prev[pid] ?? 0) - preset.mins) }));
      }
      setRecent(prev => prev.filter(r => r.id !== id));
      if (e && e.status === 409) {
        const errId = 'conf' + Date.now();
        setToast({
          id: errId,
          text: 'Patient was updated elsewhere. Reloaded latest data; try again.',
          entryId: null,
          error: true,
        });
        setTimeout(() => setToast(t => (t && t.id === errId ? null : t)), 4500);
        refetchPatients();
      } else {
        const errId = 'err' + Date.now();
        setToast({ id: errId, text: `Failed to log time: ${e?.message || 'unknown error'}`, entryId: null, error: true });
        setTimeout(() => setToast(t => (t && t.id === errId ? null : t)), 4000);
      }
    }
  }

  // addTimeBatch — Apply All path. Aggregates multiple presets for ONE patient
  // into a single PUT so we never race against ourselves. Server optimistic
  // locking still fires (single _updatedAt round-trip) — the server-side
  // hazard is another USER updating the row mid-flight, which 409s and we
  // refetch.
  async function addTimeBatch(pid, presets) {
    if (!Array.isArray(presets) || presets.length === 0) return;
    const persistedEntries = presets.map(pr => buildEntry(pid, pr));
    const totalMins = persistedEntries.reduce((s, e) => s + (Number(e.duration) || 0), 0);
    const entryIds = persistedEntries.map(e => e.id);
    const recentEntries = persistedEntries.map((e, i) => ({ ...e, mins: Number(presets[i].mins) || Number(e.duration) || 0 }));

    // Optimistic UI for the whole batch up front — matches today's single-call
    // feel. Rollback below if the (single) PUT fails. Pain lens derives the
    // honest evidence total (see addTime).
    const p = patients.find(x => x.id === pid);
    setLogged(prev => {
      if (programFilter === 'pain' && p && p._raw) {
        return { ...prev, [pid]: painLensMinutes({ ...p._raw, activityLog: [...(p._raw.activityLog || []), ...persistedEntries] }) };
      }
      return { ...prev, [pid]: (prev[pid] ?? 0) + totalMins };
    });
    const bhiBatchMins = presets.reduce((acc, p) => acc + (p.programFamily === 'bhi' ? Number(p.mins) || 0 : 0), 0);
    if (bhiBatchMins) {
      setBhiLogged(prev => ({ ...prev, [pid]: (prev[pid] ?? 0) + bhiBatchMins }));
    }
    setRecent(prev => [...recentEntries, ...prev].slice(0, 14));
    setFlashing({ pid, mins: totalMins });
    setTimeout(() => setFlashing(null), 750);
    // Toast tracks the whole visible batch, so Undo reverses exactly what the
    // confirmation text says was just logged.
    const toastId = entryIds[0];
    const summary = presets.length === 1
      ? `+${totalMins}m ${presets[0].label} → ${p ? p.name : 'patient'}`
      : `+${totalMins}m (${presets.length} entries) → ${p ? p.name : 'patient'}`;
    setToast({ id: toastId, text: summary, entryId: toastId, entryIds });
    setTimeout(() => setToast(t => (t && t.id === toastId ? null : t)), 3200);

    try {
      const patient = patients.find(x => x.id === pid);
      if (!patient || !patient._raw) throw new Error('patient data not loaded');
      const nextData = {
        ...patient._raw,
        activityLog: [...(patient._raw.activityLog || []), ...persistedEntries],
      };
      const resp = await ccmApi.updatePatient(pid, nextData);
      const fresh = resp && resp.updatedAt ? resp.updatedAt : null;
      setPatients(prev => prev.map(x => x.id === pid
        ? { ...x, _raw: { ...nextData, _updatedAt: fresh || nextData._updatedAt } }
        : x));
    } catch (e) {
      // Roll back the entire batch (pain lens: reset to the unchanged raw's
      // evidence total — see addTime).
      setLogged(prev => {
        if (programFilter === 'pain' && p && p._raw) {
          return { ...prev, [pid]: painLensMinutes(p._raw) };
        }
        return { ...prev, [pid]: Math.max(0, (prev[pid] ?? 0) - totalMins) };
      });
      if (bhiBatchMins) {
        setBhiLogged(prev => ({ ...prev, [pid]: Math.max(0, (prev[pid] ?? 0) - bhiBatchMins) }));
      }
      const idSet = new Set(entryIds);
      setRecent(prev => prev.filter(r => !idSet.has(r.id)));
      if (e && e.status === 409) {
        const errId = 'conf' + Date.now();
        setToast({
          id: errId,
          text: 'Patient was updated elsewhere. Reloaded latest data; try again.',
          entryId: null,
          error: true,
        });
        setTimeout(() => setToast(t => (t && t.id === errId ? null : t)), 4500);
        refetchPatients();
      } else {
        const errId = 'err' + Date.now();
        setToast({ id: errId, text: `Failed to log time: ${e?.message || 'unknown error'}`, entryId: null, error: true });
        setTimeout(() => setToast(t => (t && t.id === errId ? null : t)), 4000);
      }
    }
  }

  // undoEntries — optimistic removal with backend persistence + rollback on failure.
  // Same _updatedAt + 409 treatment as addTime: passes _updatedAt on the body
  // so the server's optimistic lock can fire, captures the response's new
  // timestamp, and refetches on 409.
  async function undoEntries(ids) {
    const idSet = new Set(Array.isArray(ids) ? ids : [ids]);
    const entries = recent.filter(r => idSet.has(r.id));
    if (!entries.length) return;
    const byPid = new Map();
    for (const entry of entries) {
      if (!byPid.has(entry.pid)) byPid.set(entry.pid, []);
      byPid.get(entry.pid).push(entry);
    }
    // Snapshot for rollback (use functional updaters so we capture pre-change state).
    // Pain lens: derive from the would-be log (entries removed) — see addTime.
    setLogged(prev => {
      const next = { ...prev };
      for (const [pid, rows] of byPid.entries()) {
        const patient = programFilter === 'pain' ? patients.find(x => x.id === pid) : null;
        if (patient && patient._raw) {
          const removeIds = new Set(rows.map(r => r.id));
          next[pid] = painLensMinutes({ ...patient._raw, activityLog: (patient._raw.activityLog || []).filter(e => !removeIds.has(e.id)) });
          continue;
        }
        const mins = rows.reduce((sum, entry) => sum + (Number(entry.mins) || 0), 0);
        next[pid] = Math.max(0, (next[pid] ?? 0) - mins);
      }
      return next;
    });
    setBhiLogged(prev => {
      const next = { ...prev };
      for (const [pid, rows] of byPid.entries()) {
        const mins = rows
          .filter(entry => entryProgramFamily(entry) === 'bhi')
          .reduce((sum, entry) => sum + (Number(entry.mins) || 0), 0);
        if (mins) next[pid] = Math.max(0, (next[pid] ?? 0) - mins);
      }
      return next;
    });
    setRecent(prev => prev.filter(r => !idSet.has(r.id)));
    setToast(null);

    try {
      for (const [pid, rows] of byPid.entries()) {
        const patient = patients.find(x => x.id === pid);
        if (!patient || !patient._raw) throw new Error('patient data not loaded');
        const removeIds = new Set(rows.map(entry => entry.id));
        const nextData = {
          ...patient._raw,
          activityLog: (patient._raw.activityLog || []).filter(e => !removeIds.has(e.id)),
        };
        const resp = await ccmApi.updatePatient(pid, nextData);
        const fresh = resp && resp.updatedAt ? resp.updatedAt : null;
        setPatients(prev => prev.map(x => x.id === pid
          ? { ...x, _raw: { ...nextData, _updatedAt: fresh || nextData._updatedAt } }
          : x));
      }
    } catch (e) {
      // Re-add entries locally and restore the minutes; user can retry.
      // Pain lens: restore to the evidence total of the local raw (for a pid
      // whose PUT never ran, that raw still holds the entries — honest reset).
      setLogged(prev => {
        const next = { ...prev };
        for (const [pid, rows] of byPid.entries()) {
          const patient = programFilter === 'pain' ? patients.find(x => x.id === pid) : null;
          if (patient && patient._raw) {
            next[pid] = painLensMinutes(patient._raw);
            continue;
          }
          const mins = rows.reduce((sum, entry) => sum + (Number(entry.mins) || 0), 0);
          next[pid] = (next[pid] ?? 0) + mins;
        }
        return next;
      });
      setBhiLogged(prev => {
        const next = { ...prev };
        for (const [pid, rows] of byPid.entries()) {
          const mins = rows
            .filter(entry => entryProgramFamily(entry) === 'bhi')
            .reduce((sum, entry) => sum + (Number(entry.mins) || 0), 0);
          if (mins) next[pid] = (next[pid] ?? 0) + mins;
        }
        return next;
      });
      setRecent(prev => [...entries, ...prev].slice(0, 14));
      if (e && e.status === 409) {
        const errId = 'conf' + Date.now();
        setToast({
          id: errId,
          text: 'Patient was updated elsewhere. Reloaded latest data; try again.',
          entryId: null,
          error: true,
        });
        setTimeout(() => setToast(t => (t && t.id === errId ? null : t)), 4500);
        refetchPatients();
      } else {
        const errId = 'err' + Date.now();
        setToast({ id: errId, text: `Failed to undo: ${e?.message || 'unknown error'}`, entryId: null, error: true });
        setTimeout(() => setToast(t => (t && t.id === errId ? null : t)), 4000);
      }
    }
  }

  return (
    <div className="timelogs-page">
      {/* Header strip with stats + actions */}
      <div className="tl-header">
        <div className="tl-stats">
          {programFilter === 'all' && allMetCounts ? (
            <Stat
              label="Met target"
              value={`${allMetCounts.ccmMet} CCM · ${allMetCounts.bhiMet} BHI`}
              sub={`of ${allMetCounts.ccmTotal} CCM · ${allMetCounts.bhiTotal} BHI patients`}
              ok
            />
          ) : (
            <Stat label="Met target" value={metCount} sub={`of ${slotTotal} ${programFilter === 'bhi' ? 'BHI' : programFilter === 'pain' ? 'pain-mgmt' : 'CCM'} patients`} ok />
          )}
          <Stat label="Below target" value={belowCount} sub="needs more time" warn={belowCount>0} />
          <Stat
            label="At risk"
            value={paceCounts.atRisk}
            sub={`day ${dayOfMonth} of ${daysInMonth} · behind pace for month-end`}
            warn={paceCounts.atRisk > 0}
          />
          <Stat label="Untouched" value={paceCounts.untouched} sub="no minutes logged yet" warn={paceCounts.untouched > 0} />
          <Stat label="Visible rows" value={list.length} sub="match current filters" />
        </div>
        <div className="tl-actions">
          {bhiEnabled && bhRetagCandidates.length > 0 && (
            <button
              className="btn"
              onClick={() => setBhRetagOpen(true)}
              title="Untagged entries with behavioral evidence currently counting toward CCM — review and retag to BHI">
              BH time review <span className="n">{bhRetagCandidates.length}</span>
            </button>
          )}
          <button className="btn" onClick={()=>setEditingPresets(true)}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>
            Edit presets
          </button>
          {programFilter !== 'bhi' && programFilter !== 'pain' && (
            <button className="btn accent" onClick={runScan}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M12 2v3M12 19v3M5.6 5.6l2.1 2.1M16.3 16.3l2.1 2.1M2 12h3M19 12h3M5.6 18.4l2.1-2.1M16.3 7.7l2.1-2.1"/>
                <circle cx="12" cy="12" r="3.5"/>
              </svg>
              AI scan &amp; suggest
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="tl-filters">
        <div className="tl-search">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{color:'var(--ink-3)'}}>
            <circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/>
          </svg>
          <input ref={searchInputRef} placeholder="Search patients or MRN…" value={query} onChange={e=>setQuery(e.target.value)} />
        </div>
        <div className="tl-filter-group">
          <span className="tl-filter-label">Status</span>
          {[
            { v:'below', l:'Below target', n: belowCount },
            { v:'met',   l:'Met',          n: metCount },
            // Runway lenses (CCM ladder only): base met but reimbursable
            // minutes still on the table this month.
            ...(programFilter === 'ccm' || programFilter === 'all' ? [
              { v:'addon',   l:'Add-on runway',      n: addonCount,
                t: '99490 met with a 99439 add-on unit still reachable (20–59 CCM min)' },
              { v:'complex', l:'Complex candidates', n: complexCount,
                t: 'Add-ons capped and climbing toward the 90-min tier bump, or ≥90 min with the 99487/99491 affirmation still owed' },
            ] : []),
            { v:'atrisk', l:'At risk', n: paceCounts.atRisk, hot: true,
              t: `Won't reach a minute target by month-end at the current logging rate (day ${dayOfMonth} of ${daysInMonth}). Sorted hardest-to-save first.` },
            { v:'all',   l:'All',          n: operationalPatients.length },
          ].map(f => (
            <button
              key={f.v}
              className={`filter-pill ${statusFilter===f.v?'active':''}`}
              title={f.t}
              style={f.hot && f.n > 0 && statusFilter !== f.v ? { color: 'var(--bad)', borderColor: 'var(--bad)' } : undefined}
              onClick={()=>setStatusFilter(f.v)}>
              {f.l} <span className="n">{f.n}</span>
            </button>
          ))}
        </div>
        {allTags.length > 0 && (
          <div className="tl-filter-group">
            <span className="tl-filter-label">Tag</span>
            <button className={`filter-pill ${tagFilter==='all'?'active':''}`} onClick={()=>setTagFilter('all')}>All</button>
            {allTags.map(t => (
              <button key={t} className={`filter-pill ${tagFilter===t?'active':''}`} onClick={()=>setTagFilter(t)}>{tagDisplay(t)}</button>
            ))}
          </div>
        )}
      </div>

      {/* The table */}
      <div className="tl-table card">
        <div className="tl-row tl-row-head">
          <span>Patient</span>
          <span>This month</span>
          <span>Quick log</span>
          <span></span>
        </div>
        {loading && Array.from({ length: 6 }).map((_, i) => (
          <div key={`sk${i}`} className="tl-row skeleton">
            <div className="tl-patient"><div className="sk-avatar" /><div style={{ flex: 1 }}><div className="sk-line w70" /><div className="sk-line w40" /></div></div>
            <div className="tl-progress"><div className="sk-line w50" /></div>
            <div className="tl-chips"><div className="sk-line w30" /></div>
            <div className="tl-rowend" />
          </div>
        ))}
        {list.map(p => {
          const mins = logged[p.id] ?? 0;
          const isFlash = flashing && flashing.pid === p.id;
          const isHighlighted = highlightedId === p.id;
          const recentForPatient = recent.filter(r => r.pid === p.id).slice(0, 3);
          const billedBhiCode = programFilter === 'bhi' ? resolveBhiBillCode(p._raw, month) : '';
          const isApcmAddOnMonth = billedBhiCode === 'G0570';
          const target = targetForRow(p, programFilter);
          // Pain lens: the progress number above is the evidence's billable
          // minutes; this chip surfaces the excluded remainder so a "stuck"
          // bar is self-explanatory. CPM time is provider-personal — see
          // title copy. Evidence comes from the memoized per-patient map
          // (no per-render log scans).
          const painEv = painEvByPid ? (painEvByPid[p.id] || null) : null;
          const painExcludedMins = painEv ? (painEv.otherMinutes + painEv.unknownMinutes) : 0;
          // All-lens multi-program rows get one line per enrolled family and
          // family-scoped quick-log chips; single-program rows keep today's UI.
          const slots = programFilter === 'all' ? programSlotsFor(p) : [];
          const multiSlot = slots.length >= 2;
          const selFamily = multiSlot ? selectedFamilyFor(p, slots) : null;
          const selSlot = multiSlot ? slots.find(s => s.family === selFamily) : null;
          return (
            <div
              className={`tl-row ${isHighlighted ? 'tl-row-focused' : ''}`}
              key={p.id}
              ref={(el) => { if (el) rowRefs.current[p.id] = el; }}>
              <div className="tl-patient">
                <div className={`avatar av-${p.avSlot}`}>{init(p.name)}</div>
                <div>
                  <div className="tl-name">{p.name}</div>
                  <div className="tl-sub">
                    <span className="mono">{p.mrn}</span>
                    <span style={{color:'var(--ink-4)'}}>·</span>
                    <span>{p.programLabel}</span>
                  </div>
                  {(() => {
                    // Pacing callout: how many minutes per remaining day this
                    // patient needs to stay billable — the At-risk sort key,
                    // made legible on the row itself.
                    const pace = paceByPid.get(p.id);
                    if (!pace || pace.status !== 'at-risk' || !(pace.urgency > 0)) return null;
                    const rate = pace.urgency >= 10 ? String(Math.round(pace.urgency)) : pace.urgency.toFixed(1);
                    return (
                      <div className="tabnum" style={{ fontSize: 11.5, color: 'var(--bad)', fontWeight: 600, marginTop: 2 }}>
                        needs {rate}′/day to reach target
                      </div>
                    );
                  })()}
                </div>
              </div>
              <div className="tl-progress">
                {target > 0 && multiSlot ? (
                  <>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }} role="group" aria-label="Program progress — tap a line to scope the quick-log chips">
                      {slots.map(s => {
                        const sel = s.family === selFamily;
                        const met = s.mins >= s.target;
                        return (
                          <button
                            key={s.family}
                            type="button"
                            aria-pressed={sel}
                            title={sel ? `Quick-log chips are logging to ${s.label}` : `Tap to log to ${s.label}`}
                            onClick={() => setLogFamilyByPid(prev => ({ ...prev, [p.id]: s.family }))}
                            style={{
                              display: 'grid', gridTemplateColumns: '40px 1fr auto', gap: 8,
                              alignItems: 'center', font: 'inherit', textAlign: 'left',
                              cursor: 'pointer', borderRadius: 7, padding: '3px 8px',
                              border: `1px solid ${sel ? 'var(--accent)' : 'transparent'}`,
                              background: sel ? 'var(--surface-2, rgba(0,0,0,0.04))' : 'transparent',
                            }}>
                            <span style={{
                              fontSize: 10.5, fontWeight: 700, letterSpacing: '0.07em',
                              textTransform: 'uppercase',
                              color: sel ? 'var(--accent)' : 'var(--ink-3)',
                            }}>{s.label}</span>
                            {programLineBar(s, p._raw)}
                            <span className="tabnum" style={{
                              fontSize: 12, whiteSpace: 'nowrap', fontWeight: 600,
                              color: met ? 'var(--ok)' : 'var(--warn)',
                            }}>
                              {s.mins}<span style={{ color: 'var(--ink-3)', fontWeight: 400 }}>/{s.target}′</span>
                              {s.family === 'ccm' && isCcmTierPatient(p._raw) ? (() => {
                                const prog = ccmMilestoneProgress(s.mins);
                                if (!prog.next || prog.next.min <= s.target) return null;
                                return (
                                  <span style={{ color: 'var(--ink-3)', fontWeight: 400 }} title={prog.next.title}>
                                    {' '}· {prog.next.min - prog.mins}′ to {prog.next.label}
                                  </span>
                                );
                              })() : null}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                    {isFlash && <div className="tl-flash">+{flashing.mins}m</div>}
                    {recentForPatient.length > 0 && (
                      <div className="tl-recent">
                        {recentForPatient.map(r => (
                          <span key={r.id} className="tl-recent-chip">+{r.mins}m {r.label}</span>
                        ))}
                      </div>
                    )}
                  </>
                ) : target > 0 ? (
                  <>
                    <div className={`tl-mins tabnum ${mins >= target ? 'ok' : 'warn'}`}>
                      {programFilter === 'all' && bhiEnabled && (bhiLogged[p.id] || 0) > 0 ? (
                        <span className="tl-mins-split">
                          <span className="tl-mins-ccm">CCM {(logged[p.id] || 0) - (bhiLogged[p.id] || 0)}′</span>
                          <span className="tl-mins-sep"> · </span>
                          <span className="tl-mins-bhi">BHI {bhiLogged[p.id] || 0}′</span>
                        </span>
                      ) : (
                        <span className="tl-mins-single">{mins}</span>
                      )}
                      <span style={{color:'var(--ink-3)', fontWeight:400}}>/{target} min</span>
                      {(() => {
                        // Next-milestone hint on the CCM ladder: how close the
                        // next reimbursable unit (99439 add-on / 90-min bump) is.
                        if (!(programFilter === 'ccm' || programFilter === 'all')
                            || !isCcmTierPatient(p._raw)) return null;
                        const ccmMins = programFilter === 'all' ? mins - (bhiLogged[p.id] || 0) : mins;
                        const prog = ccmMilestoneProgress(ccmMins);
                        if (!prog.next || prog.next.min <= target) return null;
                        return (
                          <span style={{color:'var(--ink-3)', fontWeight:400}} title={prog.next.title}>
                            {' '}· {prog.next.min - prog.mins}′ to {prog.next.label}
                          </span>
                        );
                      })()}
                    </div>
                    {(programFilter === 'ccm' || programFilter === 'all') && isCcmTierPatient(p._raw) ? (
                      (() => {
                        // Milestone bar: fill against the 90-min bump gate with
                        // ticks at 20/40/60/90 — red below base, yellow while
                        // add-on/complex runway remains, green at ≥90.
                        const ccmMins = programFilter === 'all' ? mins - (bhiLogged[p.id] || 0) : mins;
                        const prog = ccmMilestoneProgress(ccmMins);
                        const fill = prog.color === 'green' ? 'var(--ok)'
                          : prog.color === 'yellow' ? 'var(--warn)' : 'var(--bad)';
                        return (
                          <div className="tinybar" style={{marginTop:6, position:'relative', overflow:'visible'}}
                               title={`${prog.mins} CCM min — ticks: 20 (99490) · 40/60 (99439 add-ons) · 90 (99487/99491 bump)`}>
                            <div style={{
                              width: `${prog.pct}%`,
                              background: fill,
                              borderRadius: 'inherit',
                              transition: 'width 350ms cubic-bezier(0.16, 1, 0.3, 1)'
                            }}/>
                            {CCM_MILESTONES.map(m => (
                              <span key={m.min} title={m.title} style={{
                                position:'absolute', top:-1, bottom:-1, width:2,
                                left:`calc(${(m.min/90)*100}% - 1px)`,
                                background: prog.mins >= m.min ? 'var(--ok)' : 'var(--ink-4)',
                                borderRadius:1, opacity:0.9,
                              }}/>
                            ))}
                          </div>
                        );
                      })()
                    ) : (
                      <div className="tinybar" style={{marginTop:6}}>
                        <div style={{
                          width: `${Math.min(100, (mins/target)*100)}%`,
                          background: (() => {
                            const c = simpleTargetColor(mins, target);
                            return c === 'green' ? 'var(--ok)' : c === 'yellow' ? 'var(--warn)' : 'var(--bad)';
                          })(),
                          transition: 'width 350ms cubic-bezier(0.16, 1, 0.3, 1)'
                        }}/>
                      </div>
                    )}
                    {painExcludedMins > 0 && (
                      <div className="tl-recent">
                        <span
                          className="tl-recent-chip tl-pain-excluded-chip"
                          title="Chronic Pain Management (G3002/G3003) time counts only when personally furnished by the designated CPM billing provider (MD/DO/NP/PA/CNM). Staff, other-provider, and unattributed minutes are excluded.">
                          {painExcludedMins}m won't count (staff/other-provider/unattributed)
                        </span>
                      </div>
                    )}
                    {isFlash && <div className="tl-flash">+{flashing.mins}m</div>}
                    {recentForPatient.length > 0 && (
                      <div className="tl-recent">
                        {recentForPatient.map(r => (
                          <span key={r.id} className="tl-recent-chip">+{r.mins}m {r.label}</span>
                        ))}
                      </div>
                    )}
                  </>
                ) : isApcmAddOnMonth ? (
                  <div className="tl-mins" style={{color:'var(--ink-2)'}}>
                    <span className="tabnum">{mins}m logged</span>
                    <span className="badge addon-badge" style={{marginLeft:8}}>G0570 add-on</span>
                  </div>
                ) : (
                  <div className="tl-mins" style={{color:'var(--ink-3)'}}>APCM · flat rate</div>
                )}
              </div>
              <div className="tl-chips">
                {multiSlot && (
                  <span
                    className="tl-chip-section"
                    title="Chips below log to this program — tap another program line to switch">
                    Log to {selSlot.label}
                  </span>
                )}
                <PatientChips
                  patient={p}
                  family={selFamily}
                  presets={effectivePresets}
                  month={month}
                  labContext={
                    allLabReports == null
                      ? null
                      : { hasLabsThisMonth: (labsByPid[p.id] || 0) > 0, labCount: labsByPid[p.id] || 0 }
                  }
                  phoneNotes={
                    phoneNotesByEid && p._raw && p._raw.elation_id
                      ? (phoneNotesByEid[String(p._raw.elation_id)] || [])
                      : null
                  }
                  onLog={(pr) => addTime(p.id, pr)}
                  programFilter={programFilter}
                  settings={settings}
                  onOpenAssessment={() => setAssessmentFor(p)}
                  onOpenPainAssessment={() => setPainAssessmentFor(p)}
                />
              </div>
              <div className="tl-rowend">
                <button className="btn ghost sm" title="Edit time entries" onClick={() => setEntriesFor(p)}>›</button>
              </div>
            </div>
          );
        })}
        {!loading && patients.length === 0 && (
          <div style={{ padding: '60px 20px', textAlign: 'center', color: 'var(--ink-3)' }}>
            <p style={{ fontSize: 16, color: 'var(--ink-2)', marginBottom: 8 }}>No patients in your panel yet.</p>
            <p style={{ fontSize: 13 }}>Run <b>Elation Sync</b> from <kbd>⋯</kbd> menu to import patients.</p>
          </div>
        )}
        {!loading && patients.length > 0 && operationalPatients.length === 0 && (
          <div style={{ padding: '60px 20px', textAlign: 'center', color: 'var(--ink-3)' }}>
            <p style={{ fontSize: 16, color: 'var(--ink-2)', marginBottom: 8 }}>No patients eligible for time logging this month.</p>
            <p style={{ fontSize: 13 }}>Patients are filtered out when they're inactive, have declined consent, are Elation-marked as <code>no_patient_tags</code>, or are not enrolled in a program. Use the Onboard tab to enroll new patients.</p>
          </div>
        )}
        {!loading && operationalPatients.length > 0 && list.length === 0 && (
          <div style={{padding:'48px 20px', textAlign:'center', color:'var(--ink-3)', fontSize:14}}>
            No patients match. Adjust filters or search.
          </div>
        )}
      </div>

      {/* Toast */}
      {toast && (
        <div className={`tl-toast ${toast.error ? 'tl-toast-error' : ''}`}>
          <span className="tl-toast-text">{toast.text}</span>
          {(toast.entryIds || toast.entryId) && (
            <button className="tl-toast-undo" onClick={() => undoEntries(toast.entryIds || [toast.entryId])}>Undo</button>
          )}
        </div>
      )}

      {/* Preset editor */}
      {editingPresets && (
        <PresetEditor
          presets={programFilter === 'bhi' ? bhiPresets : presets}
          family={programFilter === 'bhi' ? 'bhi' : 'ccm'}
          month={month}
          onSave={programFilter === 'bhi' ? saveBhiPresets : savePresets}
          onClose={()=>setEditingPresets(false)}
        />
      )}

      {/* AI scan & suggest — pure UI, no backend; see runScan() above. */}
      {scanOpen && (
        <ScanSuggest
          scanning={scanning}
          suggestions={suggestions}
          patients={patients}
          model={scanModel}
          onModelChange={setScanModel}
          onRerun={runScan}
          onApply={applySuggestion}
          onApplyAll={applyAll}
          onDismiss={(sid) => setSuggestions(prev => prev.filter(s => s.sid !== sid))}
          onClose={() => { setScanOpen(false); setSuggestions([]); }}
        />
      )}

      {/* Per-patient time entries (edit/delete/add) — opens from row "›". */}
      {entriesFor && (
        <TimeEntriesModal
          patient={entriesFor}
          month={month}
          currentUser={currentUser}
          onClose={() => setEntriesFor(null)}
          onSaved={(nextData) => {
            // Re-derive the row from the new data blob: logged total + raw.
            setPatients(prev => prev.map(p => {
              if (p.id !== entriesFor.id) return p;
              const re = adaptPatient({ id: p.id, ...nextData }, month);
              return re || { ...p, _raw: nextData };
            }));
            setLogged(prev => ({
              ...prev,
              [entriesFor.id]: ccmActivityMinutes(nextData, month) + bhiActivityMinutes(nextData, month),
            }));
            setBhiLogged(prev => ({
              ...prev,
              [entriesFor.id]: bhiActivityMinutes(nextData, month),
            }));
            // Reconcile the local `recent` preview chips against the saved
            // activityLog: drop chips whose entry was deleted, and refresh
            // mins/label for chips whose entry was edited.
            const nextLog = Array.isArray(nextData.activityLog) ? nextData.activityLog : [];
            const byId = new Map(nextLog.map(e => [e.id, e]));
            setRecent(prev => prev.flatMap(r => {
              if (r.pid !== entriesFor.id) return [r];
              const match = byId.get(r.id);
              if (!match) return [];
              return [{
                ...r,
                mins: Number(match.duration) || Number(match.mins) || r.mins,
                label: match.label || match.desc || r.label,
              }];
            }));
            // Keep the modal's `patient` in sync so the list reflects the
            // post-save state without re-opening.
            setEntriesFor(prev => prev ? { ...prev, _raw: nextData } : null);
          }}
        />
      )}

      {/* Untagged-behavioral-time review — opens from the header button. */}
      {bhRetagOpen && (
        <BhiRetagModal
          candidates={bhRetagCandidates}
          onRetag={retagBhEntry}
          onClose={() => setBhRetagOpen(false)}
        />
      )}

      {/* BHI assessment modal — opens from the BHI-view row's assessment chip. */}
      {assessmentFor && (
        <BhiAssessmentModal
          patient={assessmentFor}
          settings={settings}
          currentUser={currentUser}
          onClose={() => setAssessmentFor(null)}
          onSaved={(nextData) => {
            // Refresh local patient state with the new assessment + activity log entry.
            setPatients(prev => prev.map(p => {
              if (p.id !== assessmentFor.id) return p;
              const re = adaptPatient({ id: p.id, ...nextData }, month);
              return re || { ...p, _raw: nextData };
            }));
            // Bump the minute counter immediately; works in any lens.
            setBhiLogged(prev => ({
              ...prev,
              [assessmentFor.id]: bhiActivityMinutes(nextData, month),
            }));
            setLogged(prev => ({
              ...prev,
              [assessmentFor.id]: programFilter === 'bhi'
                ? bhiActivityMinutes(nextData, month)
                : programFilter === 'all'
                  ? ccmActivityMinutes(nextData, month) + bhiActivityMinutes(nextData, month)
                  : ccmActivityMinutes(nextData, month),
            }));
          }} />
      )}

      {/* Pain assessment modal — provider/admin-only because the Pain lens is role-gated in main.jsx. */}
      {painAssessmentFor && (
        <PainAssessmentModal
          patient={painAssessmentFor}
          onClose={() => setPainAssessmentFor(null)}
          onSave={async (form) => {
            const raw = painAssessmentFor._raw || {};
            const assessmentUser = !currentUser?.credential && myCredential?.source === 'staff-list-id'
              ? { ...currentUser, credential: myCredential.credential }
              : currentUser;
            const { assessmentRecord, activityLogEntry } = buildPainAssessmentEntry(
              { ...raw, id: painAssessmentFor.id }, form, assessmentUser
            );
            const existingAssessments = Array.isArray(raw.pain?.assessments) ? raw.pain.assessments : [];
            const existingLog = Array.isArray(raw.activityLog) ? raw.activityLog : [];
            const nextData = {
              ...raw,
              pain: { ...(raw.pain || {}), assessments: [...existingAssessments, assessmentRecord] },
              activityLog: [...existingLog, activityLogEntry],
            };
            try {
              const resp = await ccmApi.updatePatient(painAssessmentFor.id, nextData);
              const fresh = resp && resp.updatedAt ? resp.updatedAt : null;
              const updated = fresh ? { ...nextData, _updatedAt: fresh } : nextData;
              setPatients(prev => prev.map(p => {
                if (p.id !== painAssessmentFor.id) return p;
                const re = adaptPatient({ id: p.id, ...updated }, month);
                return re || { ...p, _raw: updated };
              }));
              setLogged(prev => ({ ...prev, [painAssessmentFor.id]: painLensMinutes(updated) }));
              const id = 'pain-assessment-' + Date.now();
              setToast({ id, text: `Pain assessment saved for ${painAssessmentFor.name}`, entryId: null });
              setTimeout(() => setToast(t => (t && t.id === id ? null : t)), 3200);
            } catch (e) {
              if (e && e.status === 409) {
                const id = 'pain-conflict-' + Date.now();
                setToast({ id, text: 'Patient was updated elsewhere. Reloaded latest data; try again.', entryId: null, error: true });
                setTimeout(() => setToast(t => (t && t.id === id ? null : t)), 4500);
                // Close the modal: it captured the stale raw blob at open, so a
                // retry from the same instance would 409 forever. Reopening
                // binds the refetched patient.
                setPainAssessmentFor(null);
                await refetchPatients();
                return;
              }
              throw e;
            }
          }} />
      )}
    </div>
  );
}

// family: non-null on all-lens multi-program rows — scopes every chip (presets,
// AI chips, assessment, custom entry) to that program family so the logged
// entry can't land in the wrong bucket. null = today's behavior.
function PatientChips({ patient, presets, month, labContext, phoneNotes, onLog, programFilter, settings, onOpenAssessment, onOpenPainAssessment, family = null }) {
  const [open, setOpen] = useState(false);
  const [painModeOverrides, setPainModeOverrides] = useState({});
  // Popover renders with position:fixed so it escapes the .tl-table card's
  // `overflow: hidden` clip. Coords are computed from the button's viewport
  // rect at open time.
  const [pos, setPos] = useState(null);
  const ref = useRef(null);
  const btnRef = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onScrollOrResize = () => setOpen(false);
    document.addEventListener('mousedown', onDoc);
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [open]);

  function togglePopover() {
    if (!open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setPos({ top: r.bottom + 6, right: window.innerWidth - r.right });
    }
    setOpen(o => !o);
  }

  const canLogCcm = canLogPresetForPatient({ programFamily: 'ccm' }, patient);
  const canLogBhi = canLogPresetForPatient({ programFamily: 'bhi' }, patient);
  const showBhiAssessment = canLogBhi
    && (programFilter === 'bhi' || (programFilter === 'all' && (family == null || family === 'bhi')));
  const showPainAssessment = programFilter === 'pain' && isPainEnrolledPatient(patient);
  const customProgramFamily = family
    ? family
    : programFilter === 'bhi'
    ? 'bhi'
    : programFilter === 'pain'
      ? 'pain'
    : programFilter === 'ccm'
      ? 'ccm'
      : canLogCcm ? 'ccm' : canLogBhi ? 'bhi' : 'ccm';
  const customPreset = { id:'custom', mins:5, label:'Custom', programFamily: customProgramFamily };

  function effectivePainMode(preset) {
    if (!preset || preset.programFamily !== 'pain') return null;
    if (Object.prototype.hasOwnProperty.call(painModeOverrides, preset.id)) {
      return painModeOverrides[preset.id];
    }
    return isDeliveryMode(preset.mode) ? preset.mode : null;
  }

  function cyclePainMode(preset) {
    setPainModeOverrides(prev => {
      const current = Object.prototype.hasOwnProperty.call(prev, preset.id)
        ? prev[preset.id]
        : (isDeliveryMode(preset.mode) ? preset.mode : null);
      const next = current === 'telehealth' ? 'in-person' : current === 'in-person' ? null : 'telehealth';
      return { ...prev, [preset.id]: next };
    });
  }

  function painModeControl(preset) {
    const mode = effectivePainMode(preset);
    return (
      <button
        type="button"
        className="chip-popover-mode"
        onClick={() => cyclePainMode(preset)}
        aria-label={`Delivery modality for ${preset.label}: ${mode || 'unspecified'}`}
        title="Cycle delivery modality: telehealth, in-person, unspecified">
        {deliveryModeShort(mode)}
      </button>
    );
  }

  const availablePresets = useMemo(
    () => presets.filter(p =>
      (family == null || (p.programFamily || 'ccm') === family)
      && canLogPresetForPatient(p, patient)),
    [presets, patient, family]
  );

  // Custom AI chips — only HIGH-confidence patient-specific buttons (named
  // medication, named provider, lab count). When AI emits a chip for a given
  // category, the matching user preset is suppressed from the inline row
  // (the AI chip replaces it). Lower-confidence signals are handled by the
  // applicability marker below instead of synthesizing a generic chip.
  const aiChips = useMemo(
    () => (programFilter === 'bhi' || (family && family !== 'ccm'))
      ? []
      : aiChipsForPatient(patient, availablePresets, { monthKey: month, labContext, phoneNotes }),
    [patient, availablePresets, month, labContext, phoneNotes, programFilter, family]
  );

  // Preset applicability — flags inline preset chips whose category has no
  // evidence in the chart (e.g., "Lab review" with no labs this month). Used
  // to draw a "warn" outline so a clinician sees the mismatch before logging
  // time against an activity that probably didn't happen.
  const applicability = useMemo(
    () => presetApplicability(patient, availablePresets, labContext, { phoneNotes }),
    [patient, availablePresets, labContext, phoneNotes]
  );

  // Presets whose category an AI custom chip already represents — those
  // presets are suppressed inline (the AI chip takes their slot). User's
  // manual inline/order preference still drives every preset NOT covered by
  // an AI chip.
  const aiCoveredPresetIds = useMemo(
    () => new Set(aiChips.map(c => c.presetId).filter(Boolean)),
    [aiChips]
  );

  // User-controlled split: array order = inline order; `inline === false`
  // routes the preset into the More… popover. Missing `inline` defaults to
  // true so legacy preset blobs keep their visibility. Pain presets remain
  // in More even when inline: the inline chip logs the default modality, while
  // the popover is the compact override/clear surface. AI-covered presets are
  // dropped here so the AI chip stands in for them.
  const inlinePresets = availablePresets.filter(p => p.inline !== false && !aiCoveredPresetIds.has(p.id));
  const popoverPresets = availablePresets.filter(p =>
    (p.inline === false || p.programFamily === 'pain') && !aiCoveredPresetIds.has(p.id));

  return (
    <>
      {showBhiAssessment && (() => {
        const assessments = (patient._raw && patient._raw.bhi && patient._raw.bhi.assessments) || [];
        const latest = assessments[assessments.length - 1] || null;
        if (latest) {
          const sevToken = severityCssToken(latest.severity);
          return (
            <button
              type="button"
              className={`preset-chip assessment-chip ${sevToken}`}
              onClick={onOpenAssessment}
              title={`Record assessment · latest ${latest.scale} on ${latest.date}`}>
              <span className="assessment-kicker">Assessment</span>
              <b>{latest.scale}</b>
              <span className="assessment-score tabnum">{latest.score}</span>
              <span className="assessment-severity">{latest.severity || 'n/a'}</span>
            </button>
          );
        }
        // Empty state — suggest a scale (primary-styled chip).
        const suggested = suggestBhi(patient._raw || {}, settings || {});
        const label = suggested.suggestedScale || 'PHQ-9';
        return (
          <button
            type="button"
            className="preset-chip primary assessment-chip-empty"
            onClick={onOpenAssessment}>
            <span className="assessment-kicker">Assessment</span>
            <b>Record {label}</b>
          </button>
        );
      })()}
      {showPainAssessment && (() => {
        const latest = latestPainAssessment(patient._raw);
        if (latest) {
          const sevToken = severityCssToken(latest.severity);
          return (
            <button
              type="button"
              className={`preset-chip assessment-chip ${sevToken}`}
              onClick={onOpenPainAssessment}
              title={`Record pain assessment · latest ${latest.scale} on ${latest.date}`}>
              <span className="assessment-kicker">Pain assessment</span>
              <b>{latest.scale}</b>
              <span className="assessment-score tabnum">{latest.score}</span>
              <span className="assessment-severity">{latest.severity || 'n/a'}</span>
            </button>
          );
        }
        return (
          <button
            type="button"
            className="preset-chip primary assessment-chip-empty"
            onClick={onOpenPainAssessment}>
            <span className="assessment-kicker">Pain assessment</span>
            <b>Record NRS</b>
          </button>
        );
      })()}
      {aiChips.map((chip) => (
        <button
          key={chip.id}
          className="suggest-chip primary"
          onClick={() => onLog(chip)}
          title={chip.why}>
          <span className="suggest-mark" aria-hidden="true">
            <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 1l2.4 7.6L22 11l-7.6 2.4L12 21l-2.4-7.6L2 11l7.6-2.4L12 1z"/>
            </svg>
          </span>
          <b>+{chip.mins}m</b>
          <span className="suggest-label">{chip.label}</span>
        </button>
      ))}
      {(() => {
        function renderInlinePresetChips(list, key) {
          return list.map((pr) => {
            const app = applicability.get(pr.id);
            const warn = app && app.state === 'unlikely';
            const family = pr.programFamily === 'bhi' ? 'bhi' : 'ccm';
            const title = warn
              ? `Likely not applicable — ${app.why}`
              : (app && app.state === 'applies' ? app.why : (pr.hint || ''));
            return (
              <button
                key={`${key}-${pr.id}`}
                className={`preset-chip program-chip program-chip-${family} ${warn ? 'warn' : ''}`}
                onClick={() => onLog(pr)}
                title={title}>
                {warn && (
                  <span className="preset-warn-dot" aria-hidden="true" title={app.why}>!</span>
                )}
                <b>+{pr.mins}m</b>
                <span>{pr.label}</span>
              </button>
            );
          });
        }
        const ccmInline = inlinePresets.filter(p => (p.programFamily || 'ccm') === 'ccm');
        const bhiInline = inlinePresets.filter(p => p.programFamily === 'bhi');
        if (ccmInline.length > 0 && bhiInline.length > 0) {
          return (
            <>
              <span className="tl-chip-section">CCM</span>
              {renderInlinePresetChips(ccmInline, 'ccm')}
              <span className="tl-chip-section">BHI</span>
              {renderInlinePresetChips(bhiInline, 'bhi')}
            </>
          );
        }
        return renderInlinePresetChips(inlinePresets, 'preset');
      })()}
      <div className="chip-more-wrap" ref={ref}>
        <button ref={btnRef} className={`preset-chip ghost more ${open ? 'on' : ''}`} onClick={togglePopover}>
          More…
        </button>
        {open && pos && (
          <div
            className="chip-popover"
            role="menu"
            style={{ position: 'fixed', top: pos.top, right: pos.right, maxHeight: `calc(100vh - ${pos.top + 16}px)`, overflowY: 'auto' }}>
            {popoverPresets.length > 0 && (
              <>
                <div className="chip-popover-head">More time entries</div>
                {popoverPresets.map(pr => {
                  const app = applicability.get(pr.id);
                  const warn = app && app.state === 'unlikely';
                  const mode = effectivePainMode(pr);
                  const item = (
                    <button key={pr.id} className={`chip-popover-item ${warn ? 'warn' : ''}`}
                            onClick={() => { onLog(pr.programFamily === 'pain' ? { ...pr, mode } : pr); setOpen(false); }}
                            title={warn ? `Likely not applicable — ${app.why}` : (pr.hint || undefined)}>
                      {warn && <span className="preset-warn-dot" aria-hidden="true">!</span>}
                      <span className="mins tabnum">+{pr.mins}m</span>
                      <span className="label">{pr.label}</span>
                      {pr.hint && <span className="hint">{pr.hint}</span>}
                    </button>
                  );
                  if (pr.programFamily !== 'pain') return item;
                  return (
                    <div key={pr.id} className="chip-popover-entry">
                      {item}
                      {painModeControl(pr)}
                    </div>
                  );
                })}
                <div className="chip-popover-sep"/>
              </>
            )}
            {customProgramFamily === 'pain' ? (
              <div className="chip-popover-entry">
                <button className="chip-popover-item custom"
                        onClick={() => { onLog({ ...customPreset, mode: effectivePainMode(customPreset) }); setOpen(false); }}>
                  <span className="mins tabnum">+5m</span>
                  <span className="label">Custom entry…</span>
                </button>
                {painModeControl(customPreset)}
              </div>
            ) : (
              <button className="chip-popover-item custom"
                      onClick={() => { onLog(customPreset); setOpen(false); }}>
                <span className="mins tabnum">+5m</span>
                <span className="label">Custom entry…</span>
              </button>
            )}
          </div>
        )}
      </div>
    </>
  );
}

function Stat({ label, value, suffix, sub, ok, warn }) {
  return (
    <div className="tl-stat">
      <div className="label">{label}</div>
      <div className={`value tabnum ${ok?'ok':''} ${warn?'warn':''}`}>{value}{suffix && <span style={{fontSize:14, color:'var(--ink-3)', fontWeight:400, marginLeft:2}}>{suffix}</span>}</div>
      <div className="sub">{sub}</div>
    </div>
  );
}

function PresetEditor({ presets, month, onSave, onClose, family = 'ccm' }) {
  // Normalize incoming presets: legacy blobs may lack `inline` — default to
  // true so existing presets keep their on-row visibility. Use the family's own
  // normalizer so a BHI edit session keeps the `programFamily:'bhi'` stamp (and
  // would fall back to BHI, not CCM, defaults if ever handed an empty list).
  const normalizer = family === 'bhi' ? normalizeBhiPresets : normalizePresets;
  const [list, setList] = useState(() =>
    normalizer(presets).map(p => ({ ...p, inline: p.inline !== false }))
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const titleText = family === 'bhi' ? 'Edit BH activity presets' : 'Edit activity presets';
  function update(i, key, value) {
    setList(prev => prev.map((p,j) => {
      if (j !== i) return p;
      if (key === 'hint') return { ...p, hint: value, desc: value };
      return { ...p, [key]: value };
    }));
  }
  function remove(i) { setList(prev => prev.filter((_,j) => j !== i)); }
  function add() {
    setList(prev => [...prev, { id:'qlp-'+Date.now(), mins:15, label:'New preset', type:'Care Coordination', hint:'', desc:'', inline: false, programFamily: family === 'bhi' ? 'bhi' : 'ccm' }]);
  }
  function move(i, dir) {
    setList(prev => {
      const j = i + dir;
      if (j < 0 || j >= prev.length) return prev;
      const next = prev.slice();
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  }
  const inlineCount = list.filter(p => p.inline).length;

  async function save() {
    setSaving(true);
    setError('');
    try {
      await onSave?.(list);
      onClose();
    } catch (e) {
      setError(e?.message || 'Could not save presets');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-label={titleText}
        onClick={e=>e.stopPropagation()}
        style={{ maxWidth: 820 }}>
        <div className="modal-head">
          <div>
            <h3>{titleText}</h3>
            <div className="sub">
              Practice-shared. Changes apply to everyone.
              {' '}Toggle <b>Inline</b> to show a preset directly on each row; reorder controls the left-to-right order on the row.
            </div>
          </div>
          <button className="btn ghost icon" onClick={onClose} aria-label="Close">✕</button>
        </div>
        <div className="modal-body">
          <div className="preset-edit-row preset-edit-head">
            <span/>
            <span>Min</span>
            <span/>
            <span>Label</span>
            <span>Hint (optional)</span>
            <span style={{ textAlign:'center' }}>Inline</span>
            <span/>
          </div>
          {list.map((p, i) => (
            <div key={p.id} className="preset-edit-row">
              <div className="preset-edit-order">
                <button className="btn ghost icon sm" disabled={i===0} onClick={()=>move(i,-1)} title="Move up" aria-label="Move up">↑</button>
                <button className="btn ghost icon sm" disabled={i===list.length-1} onClick={()=>move(i,1)} title="Move down" aria-label="Move down">↓</button>
              </div>
              <input className="preset-edit-mins tabnum" type="number" value={p.mins}
                     onChange={e=>update(i,'mins',Math.max(1, +e.target.value))}/>
              <span style={{color:'var(--ink-3)', fontSize:13}}>min</span>
              <input className="preset-edit-label" value={p.label} onChange={e=>update(i,'label',e.target.value)}/>
              <input className="preset-edit-hint"  value={p.hint || ''}  placeholder="Optional hint" onChange={e=>update(i,'hint',e.target.value)}/>
              <label className="preset-edit-inline" title="Show this preset on the Time Logs row">
                <input
                  type="checkbox"
                  checked={!!p.inline}
                  onChange={e=>update(i,'inline', e.target.checked)} />
              </label>
              <button className="btn ghost sm" onClick={()=>remove(i)} title="Delete preset" aria-label="Delete preset">✕</button>
            </div>
          ))}
          <button className="btn sm" style={{marginTop:10}} onClick={add}>+ Add preset</button>
          {error && <div style={{ color:'var(--bad)', fontSize:13, marginTop:10 }}>{error}</div>}
        </div>
        <div className="modal-foot">
          <span style={{fontSize:12, color:'var(--ink-3)'}}>
            {list.length} preset{list.length===1?'':'s'} · {inlineCount} inline · audited
          </span>
          <div style={{display:'flex', gap:8}}>
            <button className="btn" onClick={onClose} disabled={saving}>Cancel</button>
            <button className="btn primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save presets'}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
